import React from 'react';
import { Card } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { Shield, AlertTriangle, Wifi } from 'lucide-react';

export const IPPatternDashboard: React.FC = () => {
  const ipTypeData = [
    { name: 'Data Center', value: 45, count: 892, color: '#ef4444' },
    { name: 'Residential', value: 52, count: 1034, color: '#10b981' },
    { name: 'VPN/Proxy', value: 3, count: 59, color: '#f59e0b' }
  ];

  const topDataCenters = [
    { range: '169.197.x.x', count: 234, risk: 'HIGH', provider: 'DigitalOcean', blocked: 198 },
    { range: '207.244.x.x', count: 189, risk: 'HIGH', provider: 'AWS', blocked: 156 },
    { range: '212.102.x.x', count: 145, risk: 'MEDIUM', provider: 'Hetzner', blocked: 89 },
    { range: '185.220.x.x', count: 78, risk: 'MEDIUM', provider: 'OVH', blocked: 45 }
  ];

  const recentBlocks = [
    { ip: '169.197.85.172', time: '2 min ago', reason: 'Data Center IP', location: 'Netherlands' },
    { ip: '207.244.89.166', time: '5 min ago', reason: 'AWS Instance', location: 'Virginia, US' },
    { ip: '212.102.60.16', time: '8 min ago', reason: 'Hetzner DC', location: 'Germany' }
  ];

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium">{data.name}</p>
          <p className="text-sm text-muted-foreground">{data.count} IPs ({data.value}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* IP Type Distribution */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Wifi className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">IP Type Distribution</h3>
        </div>
        
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie 
              data={ipTypeData} 
              dataKey="value" 
              cx="50%" 
              cy="50%" 
              outerRadius={80}
              label={({ name, value }) => `${name}: ${value}%`}
            >
              {ipTypeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        <div className="grid grid-cols-3 gap-4 mt-4 text-center">
          {ipTypeData.map((type, index) => (
            <div key={index} className="p-3 rounded-lg bg-gray-50">
              <div className="w-4 h-4 rounded mx-auto mb-2" style={{ backgroundColor: type.color }}></div>
              <div className="text-sm font-medium text-foreground">{type.name}</div>
              <div className="text-xs text-muted-foreground">{type.count} IPs</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Top Data Centers */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold text-foreground">High-Risk Data Centers</h3>
        </div>
        
        <div className="space-y-3">
          {topDataCenters.map((dc, index) => (
            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-medium text-foreground">{dc.range}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(dc.risk)}`}>
                    {dc.risk}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">{dc.provider} • {dc.count} total leads</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-red-600">{dc.blocked} blocked</div>
                <div className="text-xs text-muted-foreground">
                  {((dc.blocked / dc.count) * 100).toFixed(1)}% rate
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent IP Blocks */}
      <Card className="p-6 lg:col-span-2">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold text-foreground">Recent IP Blocks</h3>
          <div className="ml-auto flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-muted-foreground">Live</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {recentBlocks.map((block, index) => (
            <div key={index} className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm font-medium text-foreground">{block.ip}</span>
                <span className="text-xs text-red-600 font-medium">BLOCKED</span>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Reason: {block.reason}</div>
                <div>Location: {block.location}</div>
                <div>Time: {block.time}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-4 border-t">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">234</div>
            <div className="text-xs text-muted-foreground">Blocked Today</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">89.2%</div>
            <div className="text-xs text-muted-foreground">Block Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">45</div>
            <div className="text-xs text-muted-foreground">Unique Ranges</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">$7,020</div>
            <div className="text-xs text-muted-foreground">Losses Prevented</div>
          </div>
        </div>
      </Card>
    </div>
  );
};